import { useCallback, useEffect, useRef } from "react";

import ListEntitiesItem from "components/ListEntitiesItem";
import MetaInfoCommit from "components/MetaInfoList/Commit";
import { External, Flag, FlagFilled } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import LinkButtonIcon from "ds/components/ButtonIcon/LinkButtonIcon";
import TextEllipsis from "ds/components/TextEllipsis";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";
import { RunWithPosition } from "types/generated";
import RunStateBadge from "components/Run/elements/StateBadge";
import Timestamp from "components/Timestamp";
import useCanDiscardRun from "shared/Run/access/useCanDiscardRun";
import useCanPrioritizeRun from "shared/Run/access/useCanPrioritizeRun";

import WorkerPoolQueuedRunsListItemDropdown from "../ListItemDropdown";
import { COLUMN_GAP, COLUMN_ORDER, COLUMN_ORDER_WITHOUT_PRIORITY } from "../constants";
import useSetPriority from "../useSetPriority";
import { generateRunLink } from "./helpers";
import styles from "./styles.module.css";

type WorkerPoolQueuedRunsListItemProps = {
  queuedRun: RunWithPosition;
  setRowHeight?: (size: number) => void;
  isPublic?: boolean;
  checked: boolean;
  onCheckItem: (runId: string, isSelected: boolean) => void;
};

const WorkerPoolQueuedRunsListItem = ({
  queuedRun,
  setRowHeight,
  isPublic,
  checked,
  onCheckItem,
}: WorkerPoolQueuedRunsListItemProps) => {
  const canPrioritizeRun = useCanPrioritizeRun()({
    space: queuedRun.space,
    isRunFinished: queuedRun.run.finished,
    hasWorkerPool: true,
  });

  const rowRef = useRef<HTMLDivElement>(null);

  const { setPriority } = useSetPriority();

  const handleRowHeight = () => {
    if (setRowHeight && rowRef.current) {
      setRowHeight(rowRef.current.getBoundingClientRect().height);
    }
  };

  const handleSetPriority = useCallback(
    (isPrioritized: boolean) => () => {
      setPriority(isPrioritized, queuedRun.run.id, queuedRun.stackId);
    },
    [queuedRun.run.id, queuedRun.stackId, setPriority]
  );

  const canDiscard = useCanDiscardRun()(queuedRun.run, {
    space: queuedRun.space.id,
    spaceDetails: queuedRun.space,
  });

  // on every render, update the row height
  useEffect(handleRowHeight);

  const runLink = generateRunLink(queuedRun);

  const checkboxAriaLabel = useCallback(
    (checked: boolean) => (checked ? "Unselect this run" : "Select this run"),
    []
  );

  return (
    <ListEntitiesItem
      direction="row"
      align="center"
      justify="between"
      grid
      gridTemplate={isPublic ? COLUMN_ORDER_WITHOUT_PRIORITY : COLUMN_ORDER}
      gap={`0 ${COLUMN_GAP}`}
      ref={rowRef}
    >
      <Box direction="row" align="center">
        <Toggle
          variant="checkbox"
          id={queuedRun.run.id}
          isSelected={checked}
          onChange={(isSelected) => onCheckItem(queuedRun.run.id, isSelected)}
          aria-label={checkboxAriaLabel(checked)}
        />
      </Box>

      <Box direction="row" align="center">
        <Typography tag="span" variant="p-t7">
          {queuedRun.position}
        </Typography>
      </Box>

      <Box direction="row" align="center">
        <RunStateBadge state={queuedRun.run.state} />
      </Box>

      <Box direction="row" align="center">
        <Typography tag="span" variant="p-body3" className={styles.runType}>
          {queuedRun.run.type}
        </Typography>
      </Box>

      <Box direction="row" align="center">
        <TextEllipsis tooltip={queuedRun.stackName} tooltipWidthMode="maxWidthSm">
          {(props) => (
            <Typography {...props} tag="span" variant="p-body3">
              {queuedRun.stackName}
            </Typography>
          )}
        </TextEllipsis>
      </Box>

      <Box direction="row" align="center">
        <MetaInfoCommit commit={queuedRun.run.commit} provider={queuedRun.provider} />
      </Box>

      {!isPublic && (
        <Box direction="row" align="center">
          <ButtonIcon
            icon={queuedRun.run.isPrioritized ? FlagFilled : Flag}
            onPress={handleSetPriority(!queuedRun.run.isPrioritized)}
            variant="ghost"
            disabled={!canPrioritizeRun.result}
          >
            {canPrioritizeRun.message
              ? `Toggle priority. ${canPrioritizeRun.message}`
              : "Toggle priority"}
          </ButtonIcon>
        </Box>
      )}

      <Box direction="row" align="center">
        <Timestamp timestamp={queuedRun.run.createdAt} />
      </Box>

      <Box direction="row" align="center">
        <LinkButtonIcon icon={External} to={runLink} variant="ghost">
          Go to run
        </LinkButtonIcon>
      </Box>

      <Box direction="row" align="center">
        <WorkerPoolQueuedRunsListItemDropdown
          isPublic={isPublic}
          isPrioritized={queuedRun.run.isPrioritized}
          setPriority={handleSetPriority}
          runId={queuedRun.run.id}
          stackId={queuedRun.stackId}
          canDiscard={canDiscard}
          canPrioritize={canPrioritizeRun}
        />
      </Box>
    </ListEntitiesItem>
  );
};

export default WorkerPoolQueuedRunsListItem;
